import { useEffect, useState } from 'react'
import { collection, onSnapshot } from 'firebase/firestore'

import { firestore } from '../services/firebase/app'
import { calendarMonthRange, isPeriodLocked } from '../services/firebase/payrollPeriod'

// #1554 — a request whose month got locked is redirected into RETROACTIVE_ITEM
// (#892) without ever touching the original days[] record, which stays
// pending/pending_approval forever. Left uncounted here it would keep this
// badge one (or more) too high for good, so a locked month's timesheet is
// excluded entirely rather than filtered per day (the lock is per calendar
// month, same granularity `findPayrollPeriodCore` uses).
function countPendingApprovals(timesheetDocs, lockedPeriodStarts) {
  return (timesheetDocs || []).reduce((count, timesheetDoc) => {
    const { periodStart } = calendarMonthRange(timesheetDoc?.month, timesheetDoc?.year)
    if (lockedPeriodStarts.has(periodStart)) {
      return count
    }
    const days = Array.isArray(timesheetDoc?.days) ? timesheetDoc.days : []
    return count + days.filter((day) => ['pending', 'pending_approval'].includes(day?.editStatus)).length
  }, 0)
}

export default function usePendingApprovalsCount(employerId) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!employerId) {
      setCount(0)
      return undefined
    }

    let timesheetUnsubscribes = []
    const timesheetsByCalculation = {}
    let lockedPeriodStarts = new Set()

    const recomputeCount = () => {
      const total = Object.values(timesheetsByCalculation).reduce(
        (sum, docs) => sum + countPendingApprovals(docs, lockedPeriodStarts),
        0
      )
      setCount(total)
    }

    const clearTimesheetSubscriptions = () => {
      timesheetUnsubscribes.forEach((unsubscribe) => unsubscribe())
      timesheetUnsubscribes = []
    }

    const periodsRef = collection(firestore, 'employers', String(employerId), 'payrollPeriods')
    const unsubscribePeriods = onSnapshot(periodsRef, (snapshot) => {
      lockedPeriodStarts = new Set(
        snapshot.docs
          .map((periodDoc) => periodDoc.data())
          .filter((period) => isPeriodLocked(period))
          .map((period) => period.period_start)
      )
      recomputeCount()
    }, () => {
      lockedPeriodStarts = new Set()
      recomputeCount()
    })

    const calculationsRef = collection(firestore, 'employers', String(employerId), 'calculations')
    const unsubscribe = onSnapshot(calculationsRef, (snapshot) => {
      clearTimesheetSubscriptions()

      if (snapshot.empty) {
        Object.keys(timesheetsByCalculation).forEach((key) => delete timesheetsByCalculation[key])
        recomputeCount()
        return
      }

      snapshot.docs.forEach((calculationDoc) => {
        const calculationId = calculationDoc.id
        const timesheetsRef = collection(
          firestore,
          'employers',
          String(employerId),
          'calculations',
          String(calculationId),
          'timesheets'
        )

        const unsubscribeTimesheets = onSnapshot(timesheetsRef, (timesheetSnapshot) => {
          timesheetsByCalculation[calculationId] = timesheetSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          recomputeCount()
        })

        timesheetUnsubscribes.push(unsubscribeTimesheets)
      })
    }, () => {
      setCount(0)
      clearTimesheetSubscriptions()
    })

    return () => {
      unsubscribe()
      unsubscribePeriods()
      clearTimesheetSubscriptions()
    }
  }, [employerId])

  return count
}
